import React, { useMemo } from 'react';
import { Alert, Card, Col, Row, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Line } from '@ant-design/charts';
import type {
  DqAlert,
  KpiDay,
  KpiTotals,
  MonetizationSummary,
  RepeatCohort,
  StoreMismatch,
} from '@/types';
import { dayLabel, fmtNum, fmtPct, fmtUsd } from '../bq-insights/bqMath';
import { dayPayers, fmtPctN, freshnessLine, inr, usd4 } from './monetizationMath';
import { titleWithInfo } from './tableHelpers';
import { TILES } from './tileSpecs';
import type { TypedEntry } from './monetizationTabState';
import { TabBody } from './TabBody';
import { TargetTile } from './TargetTile';
import { TARGETS } from './targets';
import type { TargetKey } from './targets';

const { Text } = Typography;

const HEIGHT = 220;
const NO_ANIMATION = { animate: false };

/**
 * The tile of one target; shared with the Ads tab (ad impressions per DAU), which passes its own
 * tooltip text.
 */
export const SummaryTile: React.FC<{ id: TargetKey; totals: KpiTotals | null; info?: string }> = ({
  id,
  totals,
  info,
}) => {
  const spec = TILES.find((t) => t.key === id)!;
  return (
    <TargetTile
      id={id}
      label={spec.label}
      actual={totals ? totals[id] : null}
      target={TARGETS[id]}
      format={spec.format}
      info={info ?? spec.info}
    />
  );
};

const DqList: React.FC<{ alerts: DqAlert[] }> = ({ alerts }) => (
  <div data-section="dq">
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12 }}
      message={`Data-quality alerts (${alerts.length})`}
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {alerts.map((a, i) => (
            <li key={`${a.date}-${a.checkName}-${a.subject ?? ''}-${i}`} data-dq={a.checkName}>
              <b>{a.checkName}</b>
              {a.subject ? ` (${a.subject})` : ''}
              {` · ${dayLabel(a.date)}`}
              {a.value !== null && ` · value ${a.value}`}
              {a.threshold !== null && ` · threshold ${a.threshold}`}
              {a.detail ? ` · ${a.detail}` : ''}
            </li>
          ))}
        </ul>
      }
    />
  </div>
);

const MismatchList: React.FC<{ rows: StoreMismatch[] }> = ({ rows }) => (
  <div data-section="store-mismatch">
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12 }}
      message={`Store price mismatches (${rows.length})`}
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {rows.map((m) => (
            <li key={`${m.sku}-${m.store}`} data-mismatch={m.sku}>
              <b>{m.sku}</b>
              {` · ${m.store}`}
              {` · catalogue ${inr(m.catalogueInr)}`}
              {` vs store ${inr(m.storePriceInr)}`}
              {m.mismatchFields.length > 0 && ` · differs in ${m.mismatchFields.join(', ')}`}
            </li>
          ))}
        </ul>
      }
    />
  </div>
);

const SourceTag: React.FC<{ source: KpiDay['iapRevenueSource'] }> = ({ source }) => (
  <Tag color={source === 'backend' ? 'blue' : 'default'} data-source={source}>
    {source === 'backend' ? 'backend' : 'GA4'}
  </Tag>
);

/** The Payers column title's tooltip: the count follows the day's revenue source. */
export const PAYERS_INFO = 'backend payers on backend days, GA4 payers otherwise';

const dayColumns: ColumnsType<KpiDay> = [
  { title: 'Day', dataIndex: 'date', render: (d: string) => dayLabel(d) },
  {
    title: 'DAU',
    dataIndex: 'dau',
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: titleWithInfo('Payers', PAYERS_INFO),
    align: 'right',
    render: (_, row) => fmtNum(dayPayers(row)),
  },
  {
    title: 'Payer rate',
    dataIndex: 'payerRate',
    align: 'right',
    render: (v: number | null) => fmtPctN(v, 2),
  },
  {
    title: 'IAP (gross)',
    dataIndex: 'iapRevenueUsd',
    align: 'right',
    render: (v: number | null) => fmtUsd(v),
  },
  {
    title: 'Ads',
    dataIndex: 'adRevenueUsd',
    align: 'right',
    render: (v: number | null) => fmtUsd(v),
  },
  {
    title: 'Refunds',
    dataIndex: 'refundsUsd',
    align: 'right',
    render: (v: number | null) => fmtUsd(v),
  },
  {
    title: 'Revenue (net)',
    dataIndex: 'revenueUsd',
    align: 'right',
    render: (v: number | null) => fmtUsd(v),
  },
  {
    title: 'ARPDAU',
    dataIndex: 'arpdauUsd',
    align: 'right',
    render: (v: number | null) => usd4(v),
  },
  {
    title: 'Payer source',
    dataIndex: 'iapRevenueSource',
    render: (s: KpiDay['iapRevenueSource']) => <SourceTag source={s} />,
  },
];

const cohortColumns: ColumnsType<RepeatCohort> = [
  { title: 'First purchase', dataIndex: 'firstPurchaseDate', render: (d: string) => dayLabel(d) },
  {
    title: 'Payers in cohort',
    dataIndex: 'payers',
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: 'Bought again within 7 days',
    dataIndex: 'repeatPayers7d',
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: 'Repeat rate',
    dataIndex: 'repeatRate7d',
    align: 'right',
    render: (v: number | null, row) => (
      <>
        {fmtPct(v)}
        {!row.complete && (
          <Tag style={{ marginLeft: 6 }} data-incomplete="1">
            still maturing
          </Tag>
        )}
      </>
    ),
  },
];

const Content: React.FC<{ data: MonetizationSummary }> = ({ data }) => {
  const { arpdau, payer } = useMemo(() => {
    const arpdau: Array<{ day: string; value: number }> = [];
    const payer: Array<{ day: string; value: number }> = [];
    for (const d of data.days) {
      if (d.arpdauUsd !== null) arpdau.push({ day: dayLabel(d.date), value: d.arpdauUsd });
      if (d.payerRate !== null) payer.push({ day: dayLabel(d.date), value: d.payerRate });
    }
    return { arpdau, payer };
  }, [data.days]);

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }} data-freshness="1">
        {freshnessLine(data.freshness)}
      </Text>
      {data.dq.length > 0 && <DqList alerts={data.dq} />}
      {data.storeSyncMismatches.length > 0 && <MismatchList rows={data.storeSyncMismatches} />}
      <div data-section="tiles">
        <Row gutter={[12, 12]}>
          {TILES.map((t) => (
            <Col key={t.key} xs={24} sm={12} xl={6}>
              <SummaryTile id={t.key} totals={data.totals} />
            </Col>
          ))}
        </Row>
      </div>
      <div data-section="charts" style={{ marginTop: 12 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={12}>
            <Card size="small" title="Daily ARPDAU (USD, net of refunds)">
              <Line
                data={arpdau}
                xField="day"
                yField="value"
                height={HEIGHT}
                axis={{ y: { labelFormatter: (v: number) => usd4(Number(v)) } }}
                {...NO_ANIMATION}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Daily payer rate">
              <Line
                data={payer}
                xField="day"
                yField="value"
                height={HEIGHT}
                axis={{ y: { labelFormatter: (v: number) => fmtPctN(Number(v), 2) } }}
                {...NO_ANIMATION}
              />
            </Card>
          </Col>
        </Row>
      </div>
      <div data-section="days" style={{ marginTop: 12 }}>
        <Card size="small" title="By day">
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            Payer source: a blue backend day counts real purchasers from the backend; a GA4 day
            counts them from the GA4 export. Refund share and ARPPU use backend days only.
          </Text>
          <Table<KpiDay>
            size="small"
            rowKey="date"
            columns={dayColumns}
            dataSource={data.days}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </div>
      {data.repeat.length > 0 && (
        <div data-section="repeat" style={{ marginTop: 12 }}>
          <Card size="small" title="Repeat payers by first-purchase day">
            <Table<RepeatCohort>
              size="small"
              rowKey="firstPurchaseDate"
              columns={cohortColumns}
              dataSource={data.repeat}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </div>
      )}
    </div>
  );
};

interface Props {
  entry: TypedEntry<MonetizationSummary>;
  onRetry?: () => void;
}

/** Seven KPI tiles against their targets, daily trends, data-quality alerts and freshness. */
export const SummaryTab: React.FC<Props> = ({ entry, onRetry }) => (
  <TabBody entry={entry} onRetry={onRetry}>
    {(data) => <Content data={data} />}
  </TabBody>
);
